import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';

// Some GLBs (dense meshes like tpr/bbs) are Draco-compressed to stay under
// jsDelivr's 20MB per-file limit. Decoder is loaded from the same pinned
// three.js CDN version as the importmap, so it always matches.
const DRACO_DECODER_PATH = 'https://cdn.jsdelivr.net/npm/three@0.163.0/examples/jsm/libs/draco/';

// Maps product handles to GLB filenames.
// GLB URLs are resolved via window.GLB_ASSETS, injected by the host page
// (theme.liquid in prod, dev.html in local).
const MODEL_MAP = {
  'set-cerchi-gl-custom-mod-bbs': 'bbs.glb',
  'set-cerchi-gl-custom-mod-competition': 'competition.glb',
  'set-cerchi-gl-custom-mod-hannover': 'hannover.glb',
  'set-cerchi-gl-custom-mod-mustang-1': 'mustang.glb',
  'set-cerchi-gl-custom-mod-purosangue': 'purosangue.glb',
  'set-cerchi-gl-custom-mod-quarab': 'quarab-elite.glb',
  'set-cerchi-gl-custom-mod-quarab-1': 'quarab.glb',
  'set-cerchi-gl-custom-mod-quarter': 'quorter.glb',
  'tpr': 'tpr.glb',
};

// Ymq option names that drive mesh colors.
const RIM_COLOR_OPTION = 'Scegli il colore del cerchio';
const BEADLOCK_COLOR_OPTION = 'Scegli il colore del beadlock';

// Maps option value names to the actual hex color to apply on the 3D model.
// The swatches shown in the UI (ymq-option-font-color) are approximate UI tints;
// these are the paint-accurate colors rendered on the wheel geometry.
const COLOR_MAP = {
  'Bianco RAL 9003':      { hex: '#ffffff', finish: 'paint' }, // RAL 9003 Signal white@
  'Oro RAL 1036':         { hex: '#af8a39', finish: 'paint' }, // RAL 1036 Pearl gold@
  'Silver RAL 7001':      { hex: '#b4b7b0', finish: 'paint' }, // RAL 7001 Silver grey@
  'Giallo RAL 1018':      { hex: '#ffcc00', finish: 'paint' }, // RAL 1018 Zinc yellow@
  'Arancio RAL 2011':     { hex: '#fe5501', finish: 'paint' }, // RAL 2011 Deep orange@
  'Rosso RAL 3000':       { hex: '#fb1e3d', finish: 'paint' }, // RAL 3000 Flame red@
  'Blu RAL 5002':         { hex: '#003eff', finish: 'paint' }, // RAL 5002 Ultramarine blue@
  'Acquamarina RAL 6027': { hex: '#009990', finish: 'paint' }, // RAL 6027 Light green@
  'Fucsia RAL 4008':      { hex: '#bb5fc9', finish: 'paint' }, // RAL 4008 Signal violet@
  'Verde RAL 6018':       { hex: '#2ac053', finish: 'paint' }, // RAL 6018 Yellow green@
  'Nero Opaco RAL 9005':  { hex: '#16171c', finish: 'matte' }, // RAL 9005 Jet black@
  'Nero Lucido RAL 9005': { hex: '#000000', finish: 'paint' }, // RAL 9005 Jet black@
  'Viola RAL 4010':       { hex: '#c6467f', finish: 'paint' }, // RAL 4010 Telemagenta
  'Rosa RAL 3015':        { hex: '#ffb3bb', finish: 'paint' }, // RAL 3015 Light pink
  'Grigio RAL 7016':      { hex: '#333c45', finish: 'paint' }, // RAL 7016 Anthracite grey@
  'Giallo RAL 1016':      { hex: '#e5ff00', finish: 'paint' }, // RAL 1016 Sulfur yellow@
  'Cromo':                { hex: '#e6e8ea', finish: 'chrome' }, // specchio metallico — nessuna corrispondenza RAL@
};

// Initial colors shown when the product page opens before the shopper has picked
// anything (no Ymq color option selected yet). Must be keys present in COLOR_MAP,
// or null to leave the mesh at its GLB-baked color. Any real selection overrides these.
const INITIAL_RIM_COLOR = 'Bianco RAL 9003';
const INITIAL_BEADLOCK_COLOR = 'Rosso RAL 3000';

// Ymq option that toggles the whole beadlock part on/off ("Si" / "No").
const BEADLOCK_TOGGLE_OPTION = 'Beadlock';
const BEADLOCK_TOGGLE_OFF_VALUE = 'No';

// Ymq option that swaps the beadlock geometry. Each value points to a GLB filename
// resolved via window.GLB_ASSETS.
const BEADLOCK_STYLE_OPTION = 'Scegli lo stile del beadlock';
const BEADLOCK_STYLE_MAP = {
  'Con Asole': 'asole',
  'Liscio': 'liscio',
  'Fuck Edition': 'fuck',
  'Skull Edition': 'skull',
};

// Ymq option that picks the bolt pattern ("Attacco"). Its values are car names
// ending in a "<holes>x<PCD>" pattern (e.g. "Jeep TJ 5x114.3", "Dana 60 - 8x165.1").
// All current GLBs are modeled with a 5-hole bolt pattern (the default/base file,
// same convention as "Liscio" for beadlock style: base name = no suffix). Any other
// hole count is resolved to a "<base>-<holes>.glb" variant when one exists.
const ATTACCO_OPTION = 'Attacco';
const ATTACCO_DEFAULT_HOLES = '5';

// Bolt pattern shown when the page opens before the shopper picks an "Attacco"
// value. Must be a full Ymq option value ending in "<holes>x<PCD>"; a 6-hole
// value resolves to the "-6.glb" variant (see getHolesSuffixKey). Any real
// selection overrides it; null = leave at the GLB-baked 5-hole base pattern.
const INITIAL_ATTACCO = 'Giappo (Toyota / Nissan / Mitsubishi) 6x139.7';

// Extracts the hole count from an "Attacco" value like "... 6x139.7", returning
// null when it matches the default (5-hole) pattern already baked into the base GLB.
function getHolesSuffixKey(value) {
  if (!value) return null;
  const match = value.match(/(\d+)\s*x\s*[\d.]+/i);
  if (!match) return null;
  return match[1] === ATTACCO_DEFAULT_HOLES ? null : match[1];
}

// Realtime shadow — cast by a directional light onto a ShadowMaterial ground plane.
// This is additive on top of the existing blob shadow, not a replacement.
const USE_BLOB_SHADOW = false;          // true = radial gradient contact shadow; false = disabled
const USE_REALTIME_SHADOWS = true;     // true = directional shadow via ShadowMaterial; false = disabled
const SHADOW_OPACITY = 0.45;           // darkness of the cast shadow (0 = invisible, 1 = solid black)
const SHADOW_MAP_SIZE = 1024;          // shadow map resolution: 512 / 1024 / 2048 / 4096
const SHADOW_BLUR = 32;                 // softness of the shadow edge (0 = crisp, higher = more blurred)
const SHADOW_LIGHT_X = 3;             // shadow light world position (sets shadow direction)
const SHADOW_LIGHT_Y = 8;
const SHADOW_LIGHT_Z = 5;
const SHADOW_LIGHT_INTENSITY = 0;     // light contribution to the model's shading (0 = pure shadow, no fill)

// HDRI environment — change ACTIVE_HDRI to swap environment; toggle the two flags for mode.
const HDRI_MAP = {
  'christmas': 'HDRI/christmas_photo_studio_04_1k.exr',
};
const ACTIVE_HDRI = 'christmas'; // ← cambia solo questo per switchare
const USE_HDRI = true;            // true = load EXR; false = procedural RoomEnvironment only
const HDRI_AS_BACKGROUND = false;  // true = show HDRI panorama as scene background too
const HDRI_ROTATION_Y = 0;        // rotazione orizzontale in radianti (Math.PI = 180°)

// Camera / OrbitControls constraints — tune here.
const MIN_ZOOM_DISTANCE = 1.8;                 // closest the camera can get to the model
const MAX_ZOOM_DISTANCE = 4.5;                 // farthest the camera can pull back
const MIN_POLAR_ANGLE = 0.15;                  // block straight-down overhead view
const MAX_POLAR_ANGLE = Math.PI / 2 - 0.05;    // block going under the floor
const MAX_AZIMUTH_ANGLE = Math.PI * 4 / 7;     // ±100° around the front — back face unreachable

// Initial view — azimuth 0 must correspond to the wheel's front, so the ±120° clamp
// symmetrically blocks the back. Tweak MODEL_ROTATION_* to align the GLB.
// Rotation is applied in order YXZ so X = "roll around the wheel's facing axis"
// (flips top/bottom) without changing where the front points.
//  - Y: which direction the wheel's face points (sets the front)
//  - X: rolls the wheel around its facing axis (flips upside-down at ±π)
//  - Z: spins the wheel around the world Y after Y/X — usually leave at 0
const MODEL_ROTATION_X = Math.PI;              // π = upside-down flip; try 0 if not needed
const MODEL_ROTATION_Y = -Math.PI / 2;
const MODEL_ROTATION_Z = 0;
const MODEL_SCALE = 1.0;                       // moltiplicatore sulla scala automatica (>1 = più grande)
const MODEL_WORLD_Y = 0.0;                     // offset verticale in unità mondo (negativo = più in basso)
const INITIAL_DISTANCE = 3.2;                  // camera distance from the wheel at startup
const INITIAL_AZIMUTH = Math.PI / 9;           // ~20° off-front (slight oblique for product-shot feel)
const INITIAL_POLAR = Math.PI * 0.38;          // ~68° from top (just above wheel level)

// Cart-thumbnail capture — screenshot taken on add-to-cart and stored as a hidden
// line item property (`_preview`) so the cart shows the actual configuration.
// Kept small: the data URL travels inside a line item property, which has no
// officially guaranteed size limit, so we favor a compact JPEG.
const PREVIEW_CAPTURE_SIZE = 320;              // square px of the captured thumbnail
const PREVIEW_CAPTURE_QUALITY = 0.72;          // JPEG quality (0–1); lower = smaller base64

// Orange-peel clearcoat normal map — two frequency layers blended with UDN blending.
// Layer 1 = large-scale coarse blobs; Layer 2 = fine high-frequency grain.
// See the generator below for why this is built on typed arrays and not on canvas 2D.
const ORANGE_PEEL_L1_SIZE     = 128;    // field resolution (power-of-two)
const ORANGE_PEEL_L1_STRENGTH = 6;   // bump intensity
const ORANGE_PEEL_L1_BLUR     = 3;     // blob scale in pixels
const ORANGE_PEEL_L1_SEED     = 0x5eed1;
const ORANGE_PEEL_L2_SIZE     = 1024;
const ORANGE_PEEL_L2_STRENGTH = 2.0;
const ORANGE_PEEL_L2_BLUR     = 3;
const ORANGE_PEEL_L2_SEED     = 0x5eed2;
const ORANGE_PEEL_NORMAL_SCALE = new THREE.Vector2(0.1, 0.12); // clearcoatNormalScale
// Amplitude the height field is normalized to before differentiating, which makes
// the STRENGTH values above absolute instead of dependent on the blur kernel.
// Calibrated so the map's RMS slope lands on 0.1026 — what the old
// `filter: blur(3px)` pipeline produced on desktop — so the tuning above carries
// over unchanged. (Raw blurred noise sits at sd 0.0287; the old path lost ~10% of
// the gradient to 8-bit quantization, hence the lower figure here.)
const PEEL_HEIGHT_SD = 0.0252;

// Material presets — tweak per finish × role combination.
const MAT_RIM_PAINT = {
  metalness:          0.3,
  roughness:          0.5,
  clearcoat:          0.9,
  clearcoatRoughness: 0.13,
  envMapIntensity:    0.9,
};
const MAT_RIM_MATTE = {
  metalness:          0.65,
  roughness:          0.35,
  clearcoat:          1,
  clearcoatRoughness: 1,
  envMapIntensity:    1,
};
const MAT_RIM_CHROME = {
  metalness:          0.9,
  roughness:          0.25,
  clearcoat:          1,
  clearcoatRoughness: 0,
  envMapIntensity:    1.5,
};
const MAT_BEADLOCK_PAINT = {
  metalness:          0.3,
  roughness:          0.5,
  clearcoat:          0.9,
  clearcoatRoughness: 0.13,
  envMapIntensity:    0.9,
};
const MAT_BEADLOCK_MATTE = {
  metalness:          0.65,
  roughness:          0.35,
  clearcoat:          1,
  clearcoatRoughness: 1,
  envMapIntensity:    1,
};

// Classify a mesh into a colorable role based on its name.
// Tweak here if GLBs use different naming conventions.
// "Fisso" beadlock meshes are treated as rim: they should stay on the
// default/rim material instead of picking up the beadlock color.
function meshRole(meshName) {
  const n = (meshName || '').toLowerCase();
  if (n.includes('fisso')) return 'rim';
  if (n.includes('bead') || n.includes('lock')) return 'beadlock';
  return 'rim';
}

// --- Orange-peel normal map generation -------------------------------------
// Everything below runs in plain JS on typed arrays. It used to go through
// canvas 2D (`ctx.filter = 'blur(Npx)'` + `getImageData`), which is NOT portable:
//   - `CanvasRenderingContext2D.filter` landed very late on WebKit. When absent
//     the assignment is silently ignored, so the "blurred" copy was raw per-pixel
//     white noise. Central differences on that saturate (dx ~2-3 instead of ~0.05),
//     which read on iOS as a ~50x stronger peel — the rim looked wrecked.
//   - WebKit's anti-fingerprinting perturbs `getImageData` by ±1/channel. Harmless
//     normally, but here the signal *is* a neighbour difference of a blurred field
//     (~1-3/255), then multiplied by STRENGTH, so the noise dominated it.
// Both failures were silent and unreproducible in Chrome's device mode, which
// keeps Blink/Skia and the desktop GPU. Typed arrays are bit-identical everywhere.
// Add ?peeldebug=1 to the URL to compare the generated map across devices.

// Deterministic PRNG (mulberry32). Fixed seeds mean the map is byte-identical on
// every engine AND every reload, so a visual regression is always reproducible.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// One separable box-blur pass with wrap-around, via a running sum (O(n) per row).
// Wrapping is what keeps the final map seamlessly tileable under RepeatWrapping.
// `stride`/`lineStep` pick the axis without an indirect call per access, and since
// radius < size the wrap needs one branch rather than a modulo — together that is
// most of the runtime of this generator, which runs on the main thread at startup.
function boxBlurWrapPass(src, size, radius, horizontal) {
  const dst = new Float32Array(size * size);
  const width = radius * 2 + 1;
  const stride = horizontal ? 1 : size;
  const lineStep = horizontal ? size : 1;
  for (let a = 0; a < size; a++) {
    const base = a * lineStep;
    let sum = 0;
    for (let k = -radius; k <= radius; k++) sum += src[base + (((k % size) + size) % size) * stride];
    for (let b = 0; b < size; b++) {
      dst[base + b * stride] = sum / width;
      const leaving = b - radius;
      const entering = b + radius + 1;
      sum -= src[base + (leaving < 0 ? leaving + size : leaving) * stride];
      sum += src[base + (entering >= size ? entering - size : entering) * stride];
    }
  }
  return dst;
}

// Blurred white noise, normalized to a fixed amplitude.
// Two box passes per axis approximate a Gaussian of sigma ~= blurRadius closely
// enough for a bump field (variance 2*(w^2-1)/12 with w = 2r+1: r=3 -> sigma 2.83).
function buildPeelHeightField(size, blurRadius, seed) {
  const rand = mulberry32(seed);
  let buf = new Float32Array(size * size);
  for (let i = 0; i < buf.length; i++) buf[i] = rand();
  for (let pass = 0; pass < 2; pass++) {
    buf = boxBlurWrapPass(buf, size, blurRadius, true);
    buf = boxBlurWrapPass(buf, size, blurRadius, false);
  }
  // Blurring leaves an amplitude that depends on the exact kernel, so pin it to a
  // known standard deviation. This is what makes STRENGTH an absolute knob instead
  // of one that drifts with implementation details.
  let mean = 0;
  for (let i = 0; i < buf.length; i++) mean += buf[i];
  mean /= buf.length;
  let variance = 0;
  for (let i = 0; i < buf.length; i++) {
    const d = buf[i] - mean;
    variance += d * d;
  }
  variance /= buf.length;
  const sd = Math.sqrt(variance);
  const gain = sd > 1e-9 ? PEEL_HEIGHT_SD / sd : 0;
  for (let i = 0; i < buf.length; i++) buf[i] = (buf[i] - mean) * gain + 0.5;
  return buf;
}

// Unit normal of a height field at (sx, sy) by central differences, written into
// `out` as [x, y] (z is implicit and always positive). Reused scratch, no allocs.
function sampleHeightFieldNormal(h, size, strength, sx, sy, out) {
  const left  = h[sy * size + ((sx - 1 + size) % size)];
  const right = h[sy * size + ((sx + 1) % size)];
  const up    = h[((sy - 1 + size) % size) * size + sx];
  const down  = h[((sy + 1) % size) * size + sx];
  const dx = (right - left) * strength;
  const dy = (down - up) * strength;
  const len = Math.sqrt(dx * dx + dy * dy + 1);
  out[0] = dx / len;
  out[1] = dy / len;
}

// Blends two orange-peel layers using partial-derivative (UDN) blending:
// sum the xy gradients from both layers then renormalize. This correctly combines
// a coarse large-scale peel with a fine high-frequency peel into a single map.
function mixOrangePeelNormalMaps(
  { size: size1 = 128, strength: strength1 = 5.0, blurRadius: blur1 = 5, seed: seed1 = 1 } = {},
  { size: size2 = 256, strength: strength2 = 2.5, blurRadius: blur2 = 2, seed: seed2 = 2 } = {},
  outputSize = 256
) {
  const h1 = buildPeelHeightField(size1, blur1, seed1);
  const h2 = buildPeelHeightField(size2, blur2, seed2);

  const data = new Uint8Array(outputSize * outputSize * 4);
  const n1 = new Float32Array(2);
  const n2 = new Float32Array(2);

  for (let y = 0; y < outputSize; y++) {
    for (let x = 0; x < outputSize; x++) {
      sampleHeightFieldNormal(h1, size1, strength1,
        Math.floor((x / outputSize) * size1) % size1,
        Math.floor((y / outputSize) * size1) % size1, n1);
      sampleHeightFieldNormal(h2, size2, strength2,
        Math.floor((x / outputSize) * size2) % size2,
        Math.floor((y / outputSize) * size2) % size2, n2);
      const dx = n1[0] + n2[0];
      const dy = n1[1] + n2[1];
      const len = Math.sqrt(dx * dx + dy * dy + 1);
      const idx = (y * outputSize + x) * 4;
      data[idx]     = Math.round((dx / len * 0.5 + 0.5) * 255);
      data[idx + 1] = Math.round((dy / len * 0.5 + 0.5) * 255);
      data[idx + 2] = Math.round((1  / len * 0.5 + 0.5) * 255);
      data[idx + 3] = 255;
    }
  }

  // DataTexture rather than CanvasTexture: no canvas anywhere in this path.
  // Its defaults differ from CanvasTexture's, so filtering is set explicitly.
  const tex = new THREE.DataTexture(data, outputSize, outputSize, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.colorSpace = THREE.NoColorSpace;   // it's a normal map, not color data
  tex.needsUpdate = true;
  return tex;
}

// Diagnostic overlay for ?peeldebug=1. Renders the generated map alongside its
// checksum, amplitude stats and the two canvas-2D probes that used to break this
// on WebKit. Open the same URL on desktop and on a phone: the checksum and the
// stats must match exactly. If they ever diverge again, the generator regressed;
// if they match but the look differs, the cause is downstream (GPU / material).
function installPeelDebugOverlay(tex) {
  const data = tex.image.data;
  let checksum = 0;
  let absDev = 0;
  let slopeSq = 0;
  const px = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    checksum = (checksum * 31 + data[i] + data[i + 1] * 7 + data[i + 2] * 13) >>> 0;
    absDev += Math.abs(data[i] - 128) + Math.abs(data[i + 1] - 128);
    const nx = data[i]     / 255 * 2 - 1;
    const ny = data[i + 1] / 255 * 2 - 1;
    slopeSq += nx * nx + ny * ny;
  }

  // Probe A: is canvas 2D `filter` honoured? Probe B: is `getImageData` clean?
  const probe = document.createElement('canvas');
  probe.width = probe.height = 32;
  const pctx = probe.getContext('2d');
  pctx.filter = 'blur(3px)';
  const filterOk = pctx.filter === 'blur(3px)';
  pctx.filter = 'none';
  pctx.fillStyle = '#808080';
  pctx.fillRect(0, 0, 32, 32);
  const back = pctx.getImageData(0, 0, 32, 32).data;
  let lo = 255;
  let hi = 0;
  for (let i = 0; i < back.length; i += 4) {
    lo = Math.min(lo, back[i]);
    hi = Math.max(hi, back[i]);
  }

  const view = document.createElement('canvas');
  view.width = view.height = tex.image.width;
  view.getContext('2d').putImageData(
    new ImageData(new Uint8ClampedArray(data), tex.image.width, tex.image.height), 0, 0);

  const panel = document.createElement('div');
  panel.style.cssText = `position:fixed;top:8px;right:8px;z-index:99999;padding:8px;
    background:rgba(0,0,0,.82);color:#0f0;font:11px/1.5 ui-monospace,monospace;
    border-radius:6px;width:min(200px,calc(100vw - 16px));pointer-events:none`;
  view.style.cssText = 'width:100%;height:auto;display:block;margin-bottom:6px;border-radius:3px';
  panel.appendChild(view);
  const stats = document.createElement('div');
  stats.textContent = [
    `checksum   ${checksum.toString(16).padStart(8, '0')}`,
    `mean |dev| ${(absDev / (px * 2)).toFixed(3)}`,
    `rms slope  ${Math.sqrt(slopeSq / px).toFixed(4)}`,
    `ctx.filter ${filterOk ? 'OK' : 'MISSING'}`,
    `readback   ${lo === 128 && hi === 128 ? 'clean' : `NOISY ${lo}-${hi}`}`,
  ].join('\n');
  stats.style.whiteSpace = 'pre';
  panel.appendChild(stats);
  document.body.appendChild(panel);
}

// Generates a soft radial-gradient texture used as a "blob" contact shadow.
// Darker center, fades to fully transparent — no silhouette of the model.
function makeBlobShadowTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  g.addColorStop(0.0, 'rgba(0,0,0,0.55)');
  g.addColorStop(0.35, 'rgba(0,0,0,0.28)');
  g.addColorStop(0.75, 'rgba(0,0,0,0.05)');
  g.addColorStop(1.0, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(canvas);
}

function init(containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;

  // The theme carousel calls .pause()/.play() on each slide's firstElementChild.
  container.pause = () => { };
  container.play = () => { };

  // --- Variant state ---
  // Prod: product handle comes from /products/{handle} in the URL.
  // Dev fallback: ?product={handle} query param (no-op in prod).
  const handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
  const productHandle =
    handleMatch?.[1] || new URLSearchParams(window.location.search).get('product');
  const modelFile = productHandle ? MODEL_MAP[productHandle] || null : null;
  if (productHandle && !modelFile) {
    console.warn('[3D View] No model mapped for handle:', productHandle);
  }

  const variantState = {
    productHandle,
    modelFile,
    shopify: null,
    ymqOptions: {},
  };
  container.variantState = variantState;

  // Meshes grouped by role — populated when the GLB loads.
  const meshesByRole = { rim: [], beadlock: [] };
  // "Fisso" beadlock meshes: colored/materialed as rim, but still hidden
  // when the beadlock toggle is off, since they're part of the beadlock assembly.
  let fissoMeshes = [];
  // Reference to the loaded wheel model — populated when the GLB loads.
  // Needed by swapBeadlock() to host the new beadlock as a child.
  let model = null;
  // Contact-shadow ground mesh (single instance tracked so we can dispose it on reload)
  let ground = null;
  // Realtime ShadowMaterial ground plane (null when USE_REALTIME_SHADOWS is false)
  let shadowGround = null;
  // Tracks a model that's currently being loaded (to avoid races)
  let loadingModelFile = null;
  // Tracks the currently-loaded beadlock style so we don't reload the same one.
  let currentBeadlockStyle = null;
  // Tracks the currently-applied finishes so we only rebuild materials when they change.
  let currentRimFinish = null;
  let currentBeadlockFinish = null;

  function getRimFinish() {
    const colorName = variantState.ymqOptions[RIM_COLOR_OPTION]?.value;
    return COLOR_MAP[colorName]?.finish || variantState.ymqOptions[RIM_COLOR_OPTION]?.finish || 'paint';
  }

  function getBeadlockFinish() {
    const colorName = variantState.ymqOptions[BEADLOCK_COLOR_OPTION]?.value;
    return COLOR_MAP[colorName]?.finish || variantState.ymqOptions[BEADLOCK_COLOR_OPTION]?.finish || 'paint';
  }

  // Rebuilds rim materials only when the finish has actually changed.
  function applyRimFinish() {
    const finish = getRimFinish();
    if (finish === currentRimFinish) return;
    currentRimFinish = finish;
    for (const m of meshesByRole.rim) {
      const color = m.material.color.clone();
      disposeMaterial(m.material);
      m.material = makeMaterialForRole('rim', color, finish);
    }
  }

  // Rebuilds beadlock materials only when the finish has actually changed.
  function applyBeadlockFinish() {
    const finish = getBeadlockFinish();
    if (finish === currentBeadlockFinish) return;
    currentBeadlockFinish = finish;
    for (const m of meshesByRole.beadlock) {
      const color = m.material.color.clone();
      disposeMaterial(m.material);
      m.material = makeMaterialForRole('beadlock', color, finish);
    }
  }

  function applyColors() {
    const ymq = variantState.ymqOptions;
    const rimColor = COLOR_MAP[ymq[RIM_COLOR_OPTION]?.value]?.hex;
    const beadlockColor = COLOR_MAP[ymq[BEADLOCK_COLOR_OPTION]?.value]?.hex;
    const adj = window.__devColorAdjust;
    if (rimColor) meshesByRole.rim.forEach((m) => m.material.color.set(adj ? adj('rim', rimColor) : rimColor));
    if (beadlockColor) meshesByRole.beadlock.forEach((m) => m.material.color.set(adj ? adj('beadlock', beadlockColor) : beadlockColor));
  }

  function applyBeadlockVisibility() {
    const toggle = variantState.ymqOptions[BEADLOCK_TOGGLE_OPTION]?.value;
    const visible = toggle !== BEADLOCK_TOGGLE_OFF_VALUE;
    for (const m of meshesByRole.beadlock) m.visible = visible;
    for (const m of fissoMeshes) m.visible = visible;
  }

  function applyBeadlockState() {
    // Apply beadlock visibility and optionally swap the whole model
    // to a style-specific GLB if available (baseModel -> baseModel-<style>.glb).
    // Helper to build the candidate filename.
    function getStyledModelFile(baseModelFile, styleKey) {
      if (!baseModelFile || !styleKey) return null;
      const base = baseModelFile.endsWith('.glb') ? baseModelFile.slice(0, -4) : baseModelFile;
      return `${base}-${styleKey}.glb`;
    }

    applyBeadlockVisibility();

    // Determine whether a style-specific model should be used.
    let desiredModelFile = variantState.modelFile || null;
    const styleValue = variantState.ymqOptions[BEADLOCK_STYLE_OPTION]?.value;
    if (styleValue) {
      const styleKey = BEADLOCK_STYLE_MAP[styleValue];
      if (styleKey && desiredModelFile) {
        const candidate = getStyledModelFile(desiredModelFile, styleKey);
        if (window.GLB_ASSETS?.[candidate]) {
          desiredModelFile = candidate;
        }
      }
    }

    // Layer the bolt-pattern ("Attacco") suffix on top of whichever model was
    // resolved above (base or style variant). Falls back silently to the 5-hole
    // model when a "<...>-<holes>.glb" variant doesn't exist yet.
    const attaccoValue = variantState.ymqOptions[ATTACCO_OPTION]?.value;
    const holesKey = getHolesSuffixKey(attaccoValue);
    if (holesKey && desiredModelFile) {
      const base = desiredModelFile.endsWith('.glb') ? desiredModelFile.slice(0, -4) : desiredModelFile;
      const candidate = `${base}-${holesKey}.glb`;
      if (window.GLB_ASSETS?.[candidate]) {
        desiredModelFile = candidate;
      }
    }

    // If the desired model differs from the currently loaded one, load/unload.
    if (desiredModelFile !== currentModelFile) {
      loadModelFile(desiredModelFile);
      return; // loading will re-apply beadlock state once complete
    }
  }

  function onStateChange() {
    console.log('[3D View] state updated', JSON.parse(JSON.stringify(variantState)));
    applyRimFinish();
    applyBeadlockFinish();
    applyColors();
    applyBeadlockState();
  }

  // --- Shopify native variant listener ---
  const gallery = container.closest('product-gallery');
  const formId = gallery ? gallery.getAttribute('form') : null;
  const productForm = formId ? document.forms[formId] : null;

  if (productForm) {
    productForm.addEventListener('variant:change', (event) => {
      const variant = event.detail.variant;
      variantState.shopify = variant
        ? { id: variant.id, title: variant.title, options: variant.options }
        : null;
      onStateChange();
    });
  } else {
    console.warn('[3D View] Product form not found — Shopify variant tracking disabled');
  }

  // --- Ymq Product Options listener ---
  function parseYmqName(rawName) {
    const idx = rawName.lastIndexOf('-');
    return idx > 0 ? rawName.substring(0, idx) : rawName;
  }

  function readYmqEntry(el) {
    const entry = { value: el.value };
    const color = el.getAttribute('ymq-option-font-color');
    if (color) entry.color = color;
    const valueId = el.getAttribute('ymq-option-value-id');
    if (valueId) entry.valueId = valueId;
    const price = el.getAttribute('ymq-option-price');
    if (price && price !== '0') entry.price = parseFloat(price);
    const finish = el.getAttribute('data-material-finish');
    if (finish) entry.finish = finish;
    return entry;
  }

  // Seed initial colors so the wheel opens colored (not GLB-baked) before the
  // shopper picks anything. Real Ymq selections below overwrite these entries.
  if (INITIAL_RIM_COLOR && COLOR_MAP[INITIAL_RIM_COLOR]) {
    variantState.ymqOptions[RIM_COLOR_OPTION] = {
      value: INITIAL_RIM_COLOR,
      finish: COLOR_MAP[INITIAL_RIM_COLOR].finish,
    };
  }
  if (INITIAL_BEADLOCK_COLOR && COLOR_MAP[INITIAL_BEADLOCK_COLOR]) {
    variantState.ymqOptions[BEADLOCK_COLOR_OPTION] = {
      value: INITIAL_BEADLOCK_COLOR,
      finish: COLOR_MAP[INITIAL_BEADLOCK_COLOR].finish,
    };
  }
  // Seed the initial bolt pattern so the wheel opens on the 6-hole model
  // (resolves to a "-6.glb" variant). Real "Attacco" selections below overwrite it.
  if (INITIAL_ATTACCO) {
    variantState.ymqOptions[ATTACCO_OPTION] = { value: INITIAL_ATTACCO };
  }

  // Seed state from already-selected Ymq inputs (defaults / reload).
  document.querySelectorAll('.ymq-option-rc-input:checked').forEach((el) => {
    variantState.ymqOptions[parseYmqName(el.name || '')] = readYmqEntry(el);
  });
  document.querySelectorAll('.ymq-option-native-select').forEach((el) => {
    if (!el.value) return;
    variantState.ymqOptions[parseYmqName(el.name || '')] = readYmqEntry(el);
  });

  // Mobile UX: after the shopper changes an option, gently bring the 3D preview back into
  // view. On the stacked mobile layout the preview scrolls off-screen while picking colors,
  // so the result isn't visible without scrolling up. On desktop the preview is sticky and
  // already visible, so this is a no-op there. Only scrolls when the preview is mostly
  // off-screen (avoids jarring re-scrolls when it's already in view).
  const PREVIEW_SCROLL_MQ = window.matchMedia('(max-width: 999px)');
  const initTime = performance.now();
  let previewScrollTimer = null;
  function scrollPreviewIntoViewIfNeeded() {
    if (!PREVIEW_SCROLL_MQ.matches) return;
    // Ignore any synthetic change events fired during Ymq init right after load.
    if (performance.now() - initTime < 1000) return;
    clearTimeout(previewScrollTimer);
    previewScrollTimer = setTimeout(() => {
      const rect = container.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
      const ratio = rect.height ? visible / Math.min(rect.height, vh) : 0;
      if (ratio < 0.5) container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  }

  document.addEventListener('change', (event) => {
    const el = event.target;
    const isYmqSelect = el.matches && el.matches('.ymq-option-native-select');
    const isYmqInput = el.matches && el.matches('.ymq-option-rc-input');
    if (!isYmqSelect && !isYmqInput) return;

    variantState.ymqOptions[parseYmqName(el.name || '')] = readYmqEntry(el);
    onStateChange();
    scrollPreviewIntoViewIfNeeded();
  });

  // --- Scene setup ---
  // preserveDrawingBuffer keeps the WebGL back-buffer readable after render, so
  // captureConfiguredPreview() can call toDataURL() reliably regardless of frame timing
  // (needed for the add-to-cart cart thumbnail — see the submit hook below).
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  if (USE_REALTIME_SHADOWS) {
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.VSMShadowMap;   // supports a tunable blur radius (unlike PCFSoft)
  }
  renderer.domElement.style.display = 'block';
  container.appendChild(renderer.domElement);

  // --- Loading overlay ---
  // Shown while a GLB is fetched/parsed (initial load + beadlock-style swaps).
  // Without it the viewer sits empty for a few seconds during the swap and reads
  // as broken. A gradient ring spinner over a soft frosted backdrop, in the same
  // neutral studio palette as the scene, fades in/out so the wait feels intentional.
  const loadingOverlay = (() => {
    if (!document.getElementById('tc-loader-styles')) {
      const style = document.createElement('style');
      style.id = 'tc-loader-styles';
      style.textContent = `
        .tc-loader-overlay {
          position: absolute; inset: 0; z-index: 5;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 16px;
          opacity: 0; visibility: hidden; pointer-events: none;
          transition: opacity .4s ease, visibility .4s ease;
          background: radial-gradient(circle at 50% 44%, rgba(255,255,255,.4), rgba(150,150,150,.14) 70%);
          -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px);
        }
        .tc-loader-overlay.tc-visible { opacity: 1; visibility: visible; }
        .tc-loader-spinner {
          width: 58px; height: 58px; border-radius: 50%;
          background: conic-gradient(from 90deg, rgba(20,20,20,0) 0deg, rgba(20,20,20,.65) 300deg);
          -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
          mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
          animation: tc-spin .85s linear infinite;
        }
        .tc-loader-label {
          font: 600 12px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          letter-spacing: .12em; text-transform: uppercase; color: rgba(30,30,30,.7);
        }
        .tc-loader-label i { font-style: normal; animation: tc-dot 1.4s infinite; }
        .tc-loader-label i:nth-child(2) { animation-delay: .2s; }
        .tc-loader-label i:nth-child(3) { animation-delay: .4s; }
        @keyframes tc-spin { to { transform: rotate(360deg); } }
        @keyframes tc-dot { 0%, 100% { opacity: .2; } 50% { opacity: 1; } }
      `;
      document.head.appendChild(style);
    }

    const overlay = document.createElement('div');
    overlay.className = 'tc-loader-overlay';
    overlay.innerHTML =
      '<div class="tc-loader-spinner"></div>' +
      '<div class="tc-loader-label">Caricamento<i>.</i><i>.</i><i>.</i></div>';

    // The overlay is absolutely positioned inside the container; it needs the
    // container to be a positioning context. Dev sets position:relative, prod
    // position:absolute — both qualify; this guards any other host layout.
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    container.appendChild(overlay);

    let hideTimer = null;
    return {
      show() {
        clearTimeout(hideTimer);
        overlay.classList.add('tc-visible');
      },
      hide() {
        // Let the freshly-added model paint before the overlay fades away.
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => overlay.classList.remove('tc-visible'), 80);
      },
    };
  })();

  // Scene with neutral gray background for product presentation
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xa8a8a8);

  const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100);
  camera.position.set(0, 1.2, 2.5);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = MIN_ZOOM_DISTANCE;
  controls.maxDistance = MAX_ZOOM_DISTANCE;
  controls.target.set(0, 0.4, 0);
  // Vertical clamp: keeps camera above the floor (grounded illusion holds).
  controls.minPolarAngle = MIN_POLAR_ANGLE;
  controls.maxPolarAngle = MAX_POLAR_ANGLE;
  // Horizontal clamp: blocks orbiting to the back of the wheel.
  controls.minAzimuthAngle = -MAX_AZIMUTH_ANGLE;
  controls.maxAzimuthAngle = MAX_AZIMUTH_ANGLE;
  controls.mouseButtons.RIGHT = null;
  // Panning moves `controls.target`, which is the origin the polar/azimuth clamps
  // above are measured from — so a pan slides the wheel out of frame and defeats
  // them. Off entirely: one finger orbits, two fingers zoom, and nothing shifts the
  // framing. OrbitControls has no dolly-only touch mode, so TWO stays DOLLY_PAN and
  // enablePan=false neutralizes just the pan half of it (also kills arrow-key pan).
  controls.enablePan = false;
  controls.touches.ONE = THREE.TOUCH.ROTATE;
  controls.touches.TWO = THREE.TOUCH.DOLLY_PAN;
  controls.update();

  // --- Lighting ---
  // Shadow-casting directional light — present regardless of HDRI mode.
  // Intensity is kept at SHADOW_LIGHT_INTENSITY (default 0) so it doesn't alter the
  // model's shading when HDRI is active; it only drives the ShadowMaterial ground plane.
  if (USE_REALTIME_SHADOWS) {
    const shadowLight = new THREE.DirectionalLight(0xffffff, SHADOW_LIGHT_INTENSITY);
    shadowLight.position.set(SHADOW_LIGHT_X, SHADOW_LIGHT_Y, SHADOW_LIGHT_Z);
    shadowLight.castShadow = true;
    shadowLight.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
    shadowLight.shadow.radius = SHADOW_BLUR;        // blur amount (VSMShadowMap)
    shadowLight.shadow.blurSamples = 25;            // samples used for the blur — higher = smoother
    shadowLight.shadow.camera.near = 0.5;
    shadowLight.shadow.camera.far = 20;
    shadowLight.shadow.camera.left = -2;
    shadowLight.shadow.camera.right = 2;
    shadowLight.shadow.camera.top = 2;
    shadowLight.shadow.camera.bottom = -2;
    shadowLight.shadow.bias = -0.001;
    scene.add(shadowLight);
  }

  // Manual lights are only needed without HDRI. When USE_HDRI is true the environment
  // map already carries all ambient and directional information from the EXR.
  if (!USE_HDRI) {
    scene.add(new THREE.AmbientLight(0xffffff, 0.15));

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.0);
    keyLight.position.set(3, 5, 4);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-3, 2, -2);
    scene.add(fillLight);
  }

  // --- Resize ---
  function onResize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  onResize();
  new ResizeObserver(onResize).observe(container);

  // --- Environment map ---
  const pmremGenerator = new THREE.PMREMGenerator(renderer);

  if (USE_HDRI) {
    // window.HDRI_URL can be injected by the host page (theme.liquid in prod)
    // so the actual asset URL can point to Shopify CDN. Falls back to HDRI_MAP for dev.
    const hdriUrl = window.HDRI_URL ?? HDRI_MAP[ACTIVE_HDRI];

    pmremGenerator.compileEquirectangularShader();

    // Temporary procedural env while the EXR loads (avoids a black-reflection flash).
    const fallbackEnv = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = fallbackEnv;

    new EXRLoader().load(
      hdriUrl,
      (texture) => {
        const hdriEnv = pmremGenerator.fromEquirectangular(texture).texture;
        scene.environment = hdriEnv;
        scene.environmentRotation.y = HDRI_ROTATION_Y;
        if (HDRI_AS_BACKGROUND) {
          scene.background = hdriEnv;
          scene.backgroundRotation.y = HDRI_ROTATION_Y;
        }
        texture.dispose();
        fallbackEnv.dispose();
        pmremGenerator.dispose();
      },
      undefined,
      (err) => {
        console.warn('[3D View] HDRI load failed, keeping procedural env:', err);
        pmremGenerator.dispose();
      },
    );
  } else {
    scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
    pmremGenerator.dispose();
  }

  // Shared across all meshes — one texture in memory.
  const orangePeelNormal = mixOrangePeelNormalMaps(
    {
      size: ORANGE_PEEL_L1_SIZE, strength: ORANGE_PEEL_L1_STRENGTH,
      blurRadius: ORANGE_PEEL_L1_BLUR, seed: ORANGE_PEEL_L1_SEED,
    },
    {
      size: ORANGE_PEEL_L2_SIZE, strength: ORANGE_PEEL_L2_STRENGTH,
      blurRadius: ORANGE_PEEL_L2_BLUR, seed: ORANGE_PEEL_L2_SEED,
    }
  );
  // Without this the peel smears at the grazing angles that cover most of a rim.
  orangePeelNormal.anisotropy = renderer.capabilities.getMaxAnisotropy();
  if (new URLSearchParams(location.search).has('peeldebug')) {
    installPeelDebugOverlay(orangePeelNormal);
  }

  // Builds the right MeshPhysicalMaterial for a given mesh role and finish.
  // finish: 'paint' (glossy clearcoat) | 'matte' (flat powder-coat look) | 'chrome' (mirror).
  // 'chrome' is role-independent — a mirror is a mirror regardless of rim/beadlock.
  function makeMaterialForRole(role, originalColor, finish) {
    if (finish === 'chrome') {
      return new THREE.MeshPhysicalMaterial({ color: originalColor, ...MAT_RIM_CHROME });
    }
    if (role === 'beadlock') {
      const preset = finish === 'matte' ? MAT_BEADLOCK_MATTE : MAT_BEADLOCK_PAINT;
      return new THREE.MeshPhysicalMaterial({ color: originalColor, ...preset });
    }
    if (finish === 'matte') {
      return new THREE.MeshPhysicalMaterial({ color: originalColor, ...MAT_RIM_MATTE });
    }
    return new THREE.MeshPhysicalMaterial({
      color: originalColor,
      ...MAT_RIM_PAINT,
      clearcoatNormalMap: orangePeelNormal,
      clearcoatNormalScale: ORANGE_PEEL_NORMAL_SCALE,
    });
  }

  // --- Load / reload model support ---
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
  const loader = new GLTFLoader();
  loader.setDRACOLoader(dracoLoader);
  let currentModelFile = null;

  function disposeMaterial(mat) {
    if (!mat) return;
    const maps = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'envMap', 'clearcoatNormalMap'];
    try {
      maps.forEach((k) => {
        // The peel map is shared by every painted material and outlives any single
        // model, so a beadlock-style swap must not tear it down.
        if (mat[k] === orangePeelNormal) return;
        if (mat[k] && mat[k].dispose) mat[k].dispose();
      });
    } catch (e) {
      // ignore
    }
    if (mat.dispose) mat.dispose();
  }

  function disposeModel() {
    if (!model) return;
    scene.remove(model);
    model.traverse((child) => {
      if (!child.isMesh) return;
      try {
        if (child.geometry && child.geometry.dispose) child.geometry.dispose();
      } catch (e) { }
      const mat = child.material;
      if (Array.isArray(mat)) mat.forEach(disposeMaterial);
      else disposeMaterial(mat);
    });
    model = null;
    meshesByRole.rim = [];
    meshesByRole.beadlock = [];
    fissoMeshes = [];
    currentBeadlockStyle = null;
    currentModelFile = null;
  }

  // Remove and dispose the ground/contact-shadow if present
  function disposeGround() {
    if (ground) {
      try {
        if (ground.geometry && ground.geometry.dispose) ground.geometry.dispose();
        const mat = ground.material;
        if (mat) {
          if (mat.map && mat.map.dispose) mat.map.dispose();
          if (mat.dispose) mat.dispose();
        }
      } catch (e) { }
      scene.remove(ground);
      ground = null;
    }
    if (shadowGround) {
      try {
        if (shadowGround.geometry && shadowGround.geometry.dispose) shadowGround.geometry.dispose();
        if (shadowGround.material && shadowGround.material.dispose) shadowGround.material.dispose();
      } catch (e) { }
      scene.remove(shadowGround);
      shadowGround = null;
    }
  }

  function loadModelFile(modelFileName) {
    // Unload if requested to clear model
    if (!modelFileName) {
      disposeModel();
      disposeGround();
      // reset camera / controls to initial state
      camera.position.set(0, 1.2, 2.5);
      controls.target.set(0, 0.4, 0);
      controls.update();
      return;
    }

    if (modelFileName === currentModelFile || modelFileName === loadingModelFile) return; // already loaded or loading

    const modelUrl = window.GLB_ASSETS?.[modelFileName];
    if (!modelUrl) {
      console.error('[3D View] window.GLB_ASSETS missing or has no entry for', modelFileName);
      disposeModel();
      return;
    }

    // Dispose previous model and ground before loading a new one.
    disposeModel();
    disposeGround();

    // Cover the now-empty viewer until the new model is on screen.
    loadingOverlay.show();

    loadingModelFile = modelFileName;
    loader.load(
      modelUrl,
      (gltf) => {
        // If another load started meanwhile, ignore this result
        if (loadingModelFile !== modelFileName) return;
        model = gltf.scene;
        currentModelFile = modelFileName;
        loadingModelFile = null;

        // Center and normalize scale
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = (1.5 * MODEL_SCALE) / maxDim;
        model.scale.setScalar(scale);
        model.position.sub(center.multiplyScalar(scale));
        model.position.y += MODEL_WORLD_Y;
        model.rotation.order = 'YXZ';
        model.rotation.set(MODEL_ROTATION_X, MODEL_ROTATION_Y, MODEL_ROTATION_Z);

        const meshNames = [];
        model.traverse((child) => {
          if (!child.isMesh) return;
          const originalColor = child.material?.color
            ? child.material.color.clone()
            : new THREE.Color(0x888888);
          const role = meshRole(child.name);
          const finish = role === 'rim' ? getRimFinish() : role === 'beadlock' ? getBeadlockFinish() : undefined;
          child.material = makeMaterialForRole(role, originalColor, finish);
          if (USE_REALTIME_SHADOWS) child.castShadow = true;
          meshesByRole[role].push(child);
          if (child.name.toLowerCase().includes('fisso')) fissoMeshes.push(child);

          const attrs = child.geometry.attributes;
          const flags = [];
          if (!attrs.uv) flags.push('NO UV');
          if (!attrs.tangent) flags.push('no tangent');
          meshNames.push(
            `${child.name || '(unnamed)'} -> ${role}${flags.length ? `  [${flags.join(', ')}]` : ''}`
          );
        });
        console.log('[3D View] mesh classification:\n  ' + meshNames.join('\n  '));
        currentRimFinish = getRimFinish();
        currentBeadlockFinish = getBeadlockFinish();

        scene.add(model);
        applyColors();
        applyBeadlockState();

        // Reframe camera on the loaded model.
        const newBox = new THREE.Box3().setFromObject(model);
        const newCenter = newBox.getCenter(new THREE.Vector3());
        const newSize = newBox.getSize(new THREE.Vector3());
        controls.target.copy(newCenter);
        const initialOffset = new THREE.Vector3().setFromSphericalCoords(
          INITIAL_DISTANCE,
          INITIAL_POLAR,
          INITIAL_AZIMUTH,
        );
        camera.position.copy(newCenter).add(initialOffset);
        controls.update();

        const groundY = newBox.min.y;
        const groundSize = Math.max(newSize.x, newSize.z) * 2.4;
        if (USE_BLOB_SHADOW) {
          ground = new THREE.Mesh(
            new THREE.PlaneGeometry(groundSize, groundSize),
            new THREE.MeshBasicMaterial({
              map: makeBlobShadowTexture(),
              transparent: true,
              depthWrite: false,
            }),
          );
          ground.rotation.x = -Math.PI / 2;
          ground.position.set(newCenter.x, groundY + 0.001, newCenter.z);
          scene.add(ground);
        }

        if (USE_REALTIME_SHADOWS) {
          shadowGround = new THREE.Mesh(
            new THREE.PlaneGeometry(groundSize, groundSize),
            new THREE.ShadowMaterial({ opacity: SHADOW_OPACITY, transparent: true, depthWrite: false }),
          );
          shadowGround.rotation.x = -Math.PI / 2;
          shadowGround.position.set(newCenter.x, groundY + 0.002, newCenter.z);
          shadowGround.receiveShadow = true;
          scene.add(shadowGround);
        }

        // Model is on screen — fade the loader out.
        loadingOverlay.hide();
      },
      undefined,
      (err) => {
        console.error('[3D View] Failed to load model', modelUrl, err);
        if (loadingModelFile === modelFileName) loadingModelFile = null;
        loadingOverlay.hide();
      },
    );
  }

  // Initially load if we already have one
  if (modelFile) loadModelFile(modelFile);

  // --- Fullscreen zoom for the 3D preview ---
  // The theme's magnifying-glass button opens PhotoSwipe, whose data source is built
  // only from image slides (data-media-type="image"). The 3D preview is a WebGL canvas,
  // so clicking zoom over it would open the lightbox showing only the photos — never the
  // model. Here we intercept that click while the 3D slide is the active one and, instead
  // of the (image-only) lightbox, move the canvas into a full-viewport overlay. The
  // existing ResizeObserver on #three-container resizes the renderer automatically and
  // OrbitControls keep working, so the shopper gets an enlarged, rotatable 3D view — the
  // same "zoomed" experience the images get.
  (function setupFullscreenZoom() {
    const modelSlide = container.closest('.product-gallery__media[data-media-type="model"]');
    const gallery = container.closest('product-gallery');
    const carousel = container.closest('media-carousel');
    if (!modelSlide || !gallery) return;

    const originalParent = container.parentNode;

    if (!document.getElementById('tc-fs-styles')) {
      const style = document.createElement('style');
      style.id = 'tc-fs-styles';
      style.textContent = `
        .tc-fs-overlay {
          position: fixed; inset: 0; z-index: 2147483000;
          display: none; background: #000;
        }
        .tc-fs-overlay.tc-visible { display: block; }
        .tc-fs-overlay > #three-container { position: absolute; inset: 0; border-radius: 0; }
        .tc-fs-close {
          position: absolute; top: 16px; right: 16px; z-index: 6;
          width: 44px; height: 44px; border-radius: 50%;
          border: none; cursor: pointer; display: flex;
          align-items: center; justify-content: center;
          background: rgba(255,255,255,.92); color: #252627;
          box-shadow: 0 2px 12px rgba(0,0,0,.18);
          -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px);
        }
        .tc-fs-close svg { width: 20px; height: 20px; }
        body.tc-fs-lock { overflow: hidden; }
      `;
      document.head.appendChild(style);
    }

    const overlay = document.createElement('div');
    overlay.className = 'tc-fs-overlay';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'tc-fs-close';
    closeBtn.setAttribute('aria-label', 'Chiudi');
    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    overlay.appendChild(closeBtn);
    document.body.appendChild(overlay);

    // The zoom button acts on the carousel's currently selected slide; treat the model as
    // active when the carousel says so (fallback to true if the getter is unavailable).
    function isModelActive() {
      return carousel && carousel.selectedSlide ? carousel.selectedSlide === modelSlide : true;
    }

    function enter() {
      if (overlay.classList.contains('tc-visible')) return;
      overlay.appendChild(container);          // move #three-container into the overlay
      overlay.classList.add('tc-visible');
      document.body.classList.add('tc-fs-lock');
      onResize();                              // renderer fills the viewport immediately
    }
    function exit() {
      if (!overlay.classList.contains('tc-visible')) return;
      originalParent.appendChild(container);   // return the canvas to its carousel slide
      overlay.classList.remove('tc-visible');
      document.body.classList.remove('tc-fs-lock');
      onResize();
    }

    closeBtn.addEventListener('click', exit);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') exit();
    });

    // Capture phase so we run before the theme's own bubble-phase zoom handler and can
    // suppress it. Match any product-zoom-button: if it lives inside an image slide, let
    // the theme handle it; if it's the shared/floating one, act only when the 3D is active.
    gallery.addEventListener(
      'click',
      (event) => {
        const zoomBtn = event.target.closest('[is="product-zoom-button"]');
        if (!zoomBtn) return;
        const insideMedia = zoomBtn.closest('.product-gallery__media');
        const actsOnModel = insideMedia ? insideMedia === modelSlide : isModelActive();
        if (!actsOnModel) return;
        event.preventDefault();
        event.stopPropagation();
        enter();
      },
      true,
    );
  })();

  // --- Cart-thumbnail capture ---
  // Renders the current configuration at the fixed "hero" pose (same framing the
  // preview opens with, see reframing in loadModelFile) into a square off-view frame
  // and returns a compact JPEG data URL. Returns null when no model is loaded yet.
  // The whole capture is synchronous, so the intermediate resize/pose never paints:
  // the browser only composites again on the next animation frame, already restored.
  function captureConfiguredPreview() {
    if (!model) return null;

    // Save live view so we can restore it exactly after the capture.
    const prevPos = camera.position.clone();
    const prevTarget = controls.target.clone();
    const prevAspect = camera.aspect;
    const prevSize = renderer.getSize(new THREE.Vector2());   // CSS size (not buffer px)
    const prevPixelRatio = renderer.getPixelRatio();

    try {
      // Force pixelRatio 1 so the buffer is exactly PREVIEW_CAPTURE_SIZE px (keeps the
      // base64 small and predictable regardless of the device's devicePixelRatio).
      renderer.setPixelRatio(1);
      // Reframe to the initial hero pose (mirror of loadModelFile's reframing).
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      controls.target.copy(center);
      const offset = new THREE.Vector3().setFromSphericalCoords(
        INITIAL_DISTANCE,
        INITIAL_POLAR,
        INITIAL_AZIMUTH,
      );
      camera.position.copy(center).add(offset);
      camera.aspect = 1;
      camera.updateProjectionMatrix();
      camera.lookAt(center);

      renderer.setSize(PREVIEW_CAPTURE_SIZE, PREVIEW_CAPTURE_SIZE, false);
      renderer.render(scene, camera);
      return renderer.domElement.toDataURL('image/jpeg', PREVIEW_CAPTURE_QUALITY);
    } catch (err) {
      console.error('[3D View] preview capture failed', err);
      return null;
    } finally {
      // Restore the live view (pixel ratio, size, aspect, camera, controls).
      renderer.setPixelRatio(prevPixelRatio);
      renderer.setSize(prevSize.x, prevSize.y, false);
      camera.aspect = prevAspect;
      camera.updateProjectionMatrix();
      camera.position.copy(prevPos);
      controls.target.copy(prevTarget);
      controls.update();
    }
  }
  // Exposed for local testing (dev.html) — harmless in prod.
  container.captureConfiguredPreview = captureConfiguredPreview;

  // Attach the screenshot as a hidden `_preview` line item property on add-to-cart, so
  // the theme's cart can show the actual configuration (line-item.liquid reads it).
  // Both add-to-cart paths hit fetch('/cart/add.js') without firing a native form submit:
  //   - Impact's product form  → FormData body
  //   - Globo/YMQ options app   → JSON body { items: [{ id, quantity, properties }] }
  // so we wrap window.fetch (which both call at request time) instead of hooking submit.
  // Only the configured product's current variant is tagged (see addPreviewToCartAdds).
  const getExpectedVariantId = () =>
    variantState.shopify?.id ?? productForm?.querySelector('[name="id"]')?.value ?? null;
  addPreviewToCartAdds(captureConfiguredPreview, getExpectedVariantId);

  // --- Render loop ---
  function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  }
  animate();
}

// Line item property key used to carry the 3D screenshot to the cart.
// Underscore prefix keeps it hidden from customer-facing property lists (Shopify convention;
// the Impact line-item snippet already skips `_`-prefixed keys when listing properties).
const PREVIEW_PROPERTY_KEY = '_preview';

// Wraps window.fetch so a POST to /cart/add.js gets a `_preview` line item property
// carrying a screenshot of the current 3D configuration. `getPreview` is called lazily at
// request time and returns a JPEG data URL (or null when no model is loaded — then we skip).
// `getExpectedVariantId` returns the configured product's current variant id: we only tag
// items matching it, so adds of *other* products (e.g. cart-drawer upsells shown on the
// product page) don't wrongly inherit the wheel screenshot. When it's unknown, we tag all
// (best effort — never regresses the main add-to-cart flow).
// Handles both body shapes used in the wild: FormData (Impact form) and a JSON string
// (Globo/YMQ options app, shaped as { items: [{ id, quantity, properties }] }).
function addPreviewToCartAdds(getPreview, getExpectedVariantId) {
  const origFetch = window.fetch;
  if (typeof origFetch !== 'function' || window.__tcCartFetchPatched) return;
  window.__tcCartFetchPatched = true;

  const isCartAdd = (url, method) =>
    method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url);

  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (isCartAdd(url, method) && init && init.body != null) {
        const dataUrl = getPreview();
        if (dataUrl) {
          const expectedId = getExpectedVariantId ? getExpectedVariantId() : null;
          const matches = (itemId) => expectedId == null || String(itemId) === String(expectedId);
          const body = init.body;
          if (typeof FormData !== 'undefined' && body instanceof FormData) {
            if (matches(body.get('id'))) {
              body.delete(`properties[${PREVIEW_PROPERTY_KEY}]`);
              body.append(`properties[${PREVIEW_PROPERTY_KEY}]`, dataUrl);
            }
          } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
            if (matches(body.get('id'))) body.set(`properties[${PREVIEW_PROPERTY_KEY}]`, dataUrl);
          } else if (typeof body === 'string') {
            let json = null;
            try { json = JSON.parse(body); } catch (e) { json = null; }
            if (json && typeof json === 'object') {
              if (Array.isArray(json.items)) {
                json.items.forEach((it) => {
                  if (matches(it.id)) {
                    it.properties = Object.assign({}, it.properties, { [PREVIEW_PROPERTY_KEY]: dataUrl });
                  }
                });
              } else if (matches(json.id)) {
                json.properties = Object.assign({}, json.properties, { [PREVIEW_PROPERTY_KEY]: dataUrl });
              }
              init = Object.assign({}, init, { body: JSON.stringify(json) });
            }
          }
        }
      }
    } catch (err) {
      console.error('[3D View] add-to-cart preview injection failed', err);
    }
    return origFetch.call(this, input, init);
  };
}

// Expose the initializer to global scope so themes that expect a
// non-module entrypoint (like older Shopify setups) can call it.
// Keep the module-friendly `init` function, but don't auto-run it.
window.initThreeCube = init;
